// Libraries
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { addHours, addSeconds, differenceInDays, differenceInHours, differenceInSeconds, format, getHours, getMinutes, getSeconds, startOfDay } from 'date-fns';

@Component({
  selector: 'lib-slider',
  imports: [
    CommonModule
  ],
  standalone: true,
  templateUrl: './slider.component.html',
  styleUrl: './slider.component.scss'
})

export class SliderComponent implements OnChanges, AfterViewInit, OnDestroy {
  // Component API
  @ViewChild("slider", { static: false }) slider!: ElementRef<HTMLDivElement>;
  @Input() min = 0;
  @Input() max = 100;
  @Input() width = 400;
  @Input() realtime = false;
  @Input() value = 0;
  @Input() buffer = 0;
  @Input() showTicks: boolean = true;
  @Input() startDate!: string;
  @Input() endDate!: string;
  @Output() valueChange = new EventEmitter<number>();

  // Variables
  ticks: {
    label: string,
    position: number,
    labelPosition: number
  }[] = [];
  isLive: boolean = false;
  private dragging = false;
  private readonly zone: NgZone = inject(NgZone);
  // Every mouse move of the page reaches the document: outside Angular, it only comes back in while dragging.
  private readonly move = (event: MouseEvent | TouchEvent) => {
    if (this.dragging) this.zone.run(() => this.onDrag(event));
  };

  constructor() {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.move);
      document.addEventListener('touchmove', this.move, { passive: false });
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.move);
    document.removeEventListener('touchmove', this.move);
  }

  ngAfterViewInit(): void {
    this.updateWidth();
    this.updateTicks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateWidth();
    this.updateTicks();
    this.isLive = this.value == this.max
  }

  // Listeners
  @HostListener('window:resize', ['$event']) onResize(event: any) {
    this.updateWidth();
    this.updateTicks();
  }

  @HostListener('window:toggle-sidenav', ['$event']) onToggleSidenav(event: any) {
    setTimeout(() => {
      this.updateWidth();
      this.updateTicks();
    }, 300);
  }

  startDrag(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.dragging = true;
    this.slider.nativeElement.classList.add('dragging');
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  stopDrag() {
    this.dragging = false;

    if (this.slider?.nativeElement) {
      this.slider.nativeElement.classList.remove('dragging');
    }

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  onDrag(event: MouseEvent | TouchEvent) {
    if (!this.dragging) return;

    event.preventDefault();
    this.updateValueFromEvent(event);
  }

  onTrackClick(event: MouseEvent) {
    this.updateValueFromEvent(event);
  }

  public forceStopDrag() {
    this.dragging = false;

    if (this.slider?.nativeElement) {
      this.slider.nativeElement.classList.remove('dragging');
    }

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  updateWidth() {
    if (this.slider?.nativeElement?.offsetParent) {
      let parentWidth = (this.slider.nativeElement?.offsetParent as HTMLElement).offsetWidth;
      this.width = parentWidth;// * 0.96;
    } else
      this.width = window.innerWidth * 0.80;
  }

  get playedPercentage(): number {
    return this.toPercentage(this.value);
  }

  get bufferPercentage(): number {
    return this.toPercentage(this.buffer);
  }

  private toPercentage(val: number): number {
    if (this.max == this.min) return 0;
    return ((val - this.min) / (this.max - this.min)) * 100;
  }

  private updateValueFromEvent(event: MouseEvent | TouchEvent) {
    const rect = this.slider.nativeElement.getBoundingClientRect();

    const clientX = event instanceof MouseEvent
      ? event.clientX
      : event.touches[0]?.clientX || event.changedTouches[0]?.clientX;

    if (clientX === undefined) return;

    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const newValue = Math.round(this.min + ratio * (this.max - this.min));

    if (newValue >= this.max) {
      this.dragging = false;
    }

    this.valueChange.emit(newValue);
    return newValue;
  }

  updateTicks() {
    let numberOfDays = differenceInDays(this.endDate, this.startDate);
    let result: { labelArray: string[]; ticksPositions: number[]; labelPositions: number[] } = { labelArray: [], ticksPositions: [], labelPositions: [] };
    let secondsArray: number[] = [];

    if (numberOfDays <= 1) {

      secondsArray = this.getSecondsArray(1);
      result = this.getLabelsAndPositions(secondsArray, "HH'h'")

      //set first and last
      result.labelArray[0] = format(this.startDate, "HH'h'mm");
      if (result.labelArray.length > 0 && result.labelArray[result.labelArray.length - 1])
        result.labelArray[result.labelArray.length - 1] = format(this.endDate, "HH'h'mm");

    } else if (numberOfDays > 1 && numberOfDays <= 3) {

      secondsArray = this.getSecondsArray(6);
      result = this.getLabelsAndPositions(secondsArray, "dd-MM HH'h'")

      //set first and last
      result.labelArray[0] = format(this.startDate, "dd-MM HH'h'");
      if (result.labelArray.length > 0 && result.labelArray[result.labelArray.length - 1])
        result.labelArray[result.labelArray.length - 1] = format(this.endDate, "dd-MM HH'h'");

    } else if (numberOfDays > 3 && numberOfDays <= 15) {

      secondsArray = this.getSecondsArray(24);
      result = this.getLabelsAndPositions(secondsArray, "dd-MM")

      //set first and last
      result.labelArray[0] = format(this.startDate, "dd-MM HH'h'");
      if (result.labelArray.length > 0 && result.labelArray[result.labelArray.length - 1])
        result.labelArray[result.labelArray.length - 1] = format(this.endDate, "dd-MM HH'h'");

    } else if (numberOfDays > 15) {

      secondsArray = [0, this.max];
      secondsArray.forEach(t => {

        result.ticksPositions.push((t / this.max) * 100);
        let hour = format(addSeconds(this.startDate, t), "dd-MM")
        result.labelArray.push(hour)

      })

    }
    this.ticks = [];
    result.labelArray.forEach((l, i) => {
      this.ticks.push({
        label: l,
        position: result.ticksPositions[i] || 0,
        labelPosition: result.labelPositions[i] || 0
      })
    })
  }

  /**
     * 
     * @param {number} iteration number of hour for to iterate (1, 6, 24)
     * @returns array of seconds amount
     * 
     */
  getSecondsArray(iteration = 1) {

    let secondsArray = [0];
    let ellapse = iteration * 3600;

    let diff = differenceInHours(this.endDate, this.startDate);
    diff = Math.floor(diff / iteration);

    //if hour isn't on iteration we must find the next and prev one...
    function getIterations(hour: number) {
      let prevHour = hour;
      let nextHour = hour + iteration;
      if (hour % iteration !== 0) {
        prevHour = 0;
        nextHour = 0;
        while (nextHour < hour) {
          nextHour += iteration;
          if (nextHour < hour)
            prevHour = nextHour;
        }
      }
      return { prevHour, nextHour }
    }

    //get the diff between startDate and next iteration
    let startHour = getHours(this.startDate);
    let endHour = getHours(this.endDate);

    let startIterations = getIterations(startHour);
    let endIterations = getIterations(endHour);

    let firstIteration = addHours(startOfDay(this.startDate), startIterations.prevHour);
    let lastIteration = addHours(startOfDay(this.endDate), endIterations.prevHour);

    let startDiff = differenceInSeconds(this.startDate, firstIteration);
    let endDiff = differenceInSeconds(this.endDate, lastIteration);

    for (let i = 0; i <= diff; i++) {

      if (i == 0) {//get first interval

        let minuteFromZero = getMinutes(this.startDate);
        let secondFromZero = (startHour * 3600) + (minuteFromZero * 60) + getSeconds(this.startDate);

        secondsArray.push((startIterations.nextHour * 3600) - secondFromZero)

      } else if ((i == diff && startDiff > endDiff) || i < diff) {// add interval

        secondsArray.push((secondsArray[secondsArray.length - 1] || 0) + ellapse);

      }
    }

    // set last data
    if (secondsArray[secondsArray.length - 1] !== this.max) secondsArray.push(this.max)

    return secondsArray;

  }

  getLabelsAndPositions(secondsArray: number[], formatStr: string) {
    let labelArray: string[] = [], ticksPositions: number[] = [], labelPositions: number[] = [];
    // let parentWidth = (this.slider.nativeElement?.offsetParent as HTMLElement).offsetWidth;
    // this.width = parentWidth;
    secondsArray.forEach((t, i) => {

      // let newPos = (t / this.max) * 100;
      let newPos = Math.floor((t / this.max) * this.width);
      let labelPos = i == 0 ? newPos : newPos - 5;
      ticksPositions.push(newPos);
      labelPositions.push(labelPos);
      //if interval is lower than a percent we skip the label to avoid text overlap
      if (i == 0 || (i > 0 && newPos - (ticksPositions[i - 1] || 0) > 4)) {
        let hour = format(addSeconds(this.startDate, t), formatStr)
        labelArray.push(hour)
      } else {
        labelArray.push("")
      }
    });
    if (labelPositions.length > 1) { }
    labelPositions[labelPositions.length - 1] = (labelPositions[labelPositions.length - 1] ?? 0) - 10;
    return { labelArray, ticksPositions, labelPositions }
  }
}
